import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import FlightCard from "../../components/flights/FlightCard";
import SearchForm from "../../components/flights/SearchForm";
import Header from "../../components/flights/Header";
import LoadingSkeleton, {
  PaginationSkeleton,
} from "../../components/flights/LoadingSkeleton";
import { config } from "../../config";
import Filters from "../../components/flights/Filters";

function SearchResults() {
  const [searchParams] = useSearchParams();
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredFlights, setFilteredFlights] = useState([]);

  const origin = searchParams.get("origin") || "";
  const destination = searchParams.get("destination") || "";
  const departureStr = searchParams.get("departure_date");
  const returnStr = searchParams.get("return_date");

  const departureDate = departureStr ? new Date(departureStr) : null;
  const returnDate = returnStr ? new Date(returnStr) : null;

  const adults = parseInt(searchParams.get("adults") || "1");
  const children = parseInt(searchParams.get("children") || "0");
  const infants = parseInt(searchParams.get("infants") || "0");
  const cabinClass = searchParams.get("cabin_class") || "economy";

  useEffect(() => {
    const fetchFlights = async () => {
      try {
        setLoading(true);
        setError(null);

        const searchData = {
          origin,
          destination,
          departure_date: departureStr,
          return_date: returnStr,
          adults,
          children,
          infants,
          cabin_class: cabinClass,
        };

        console.log("🔍 Search Request:", searchData);

        const response = await fetch(
          `${config.backendServer}/api/offers/search`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(searchData),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to fetch flight offers");
        }
        const data = await response.json();
        console.log("Flights Search Response:", data);

        setFlights(data.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching flights:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchFlights();
  }, [
    origin,
    destination,
    departureStr,
    returnStr,
    adults,
    children,
    infants,
    cabinClass,
  ]);

  return (
    <div className="min-h-screen bg-slate-800">
      <Header isTransparent={true} />

      <div className="container mx-auto py-8">
        <div className="mb-8">
          <SearchForm
            initialOrigin={origin}
            initialDestination={destination}
            initialDateRange={[departureDate, returnDate]}
            initialPassengers={{
              adults,
              children,
              infants,
              class: cabinClass.charAt(0).toUpperCase() + cabinClass.slice(1),
            }}
          />
        </div>

        <div className="grid md:grid-cols-4 lg:grid-cols-4 gap-4">
          <div className="col-span-1 ml-2">
            <Filters
              results={flights}
              setFilteredResults={setFilteredFlights}
            />{" "}
          </div>

          <div className="col-span-3 space-y-4 bg-gray-100 p-4 mx-2 rounded-lg">
            {loading && (
              <div>
                {/* Loading Skeleton */}
                <div className="mb-4">
                  <div className="h-6 w-48 bg-gray-300 rounded animate-pulse mb-2"></div>
                  <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
                </div>
                {[1, 2, 3].map((i) => (
                  <LoadingSkeleton key={i} />
                ))}
                <PaginationSkeleton />
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="text-center py-8">
                <div className="bg-red-50 rounded-lg p-4 mb-4">
                  <p className="text-red-600 font-medium">{error}</p>
                  <p className="text-red-500 text-sm mt-1">
                    Please try adjusting your search criteria or try again
                    later.
                  </p>
                </div>
              </div>
            )}

            {/* Results */}
            {!loading && !error && filteredFlights.length > 0 && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    {filteredFlights.length}{" "}
                    {filteredFlights.length === 1 ? "flight" : "flights"} found
                  </h2>
                </div>

                {filteredFlights.map((flight) => (
                  <FlightCard key={flight.id} flight={flight} />
                ))}
              </div>
            )}

            {/* No Results State */}
            {!loading && !error && filteredFlights.length === 0 && (
              <div className="text-center py-8">
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-yellow-700 font-medium">
                    No flights found for your search criteria
                  </p>
                  <p className="text-yellow-600 text-sm mt-1">
                    Try adjusting your filters...
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchResults;
